import {
  CampaignPlanRequestDTO,
  CampaignPostDTO,
  CampaignStatus,
  ContentInventoryDTO,
  ContentKind,
  ContentKindVisualsDTO,
  CreateCampaignPostDTO,
  CreateSocialCampaignDTO,
  GenerateSocialCampaignPostsDTO,
  PostTextAction,
  PostTextStatus,
  PostsNotPublishableDTO,
  SocialCampaignDTO,
  StoryStatus,
  UpdateCampaignPostDTO,
  UpdateSocialCampaignSettingsDTO,
  VisualKeyDTO
} from 'models/socialCampaign'
import { EntityKeys, QueryType, getMutation } from 'utils/reactQuery'
import { QueryClient, useQuery, useQueryClient } from '@tanstack/react-query'
import { _getUrl, useAPI } from 'utils/API/useApi'
import axios, { AxiosRequestConfig } from 'axios'
import { useCallback, useSyncExternalStore } from 'react'

import API from 'utils/API/API'
import { Auth0Roles } from 'models/auth'
import { RoleMimetype } from 'constants/API/roleMimetypes'
import { SocialCampaignEndpoint } from 'constants/API/endpoints'
import { postTextStatusOf } from 'utils/socialCampaign/postEditor'
import { useAuth0 } from 'utils/auth'

/**
 * Marketing Automation (epic ISSUE-3615): the social media campaign of a property.
 *
 * Every write answers the whole campaign, which goes straight into the cache before the campaign
 * query is invalidated: the page shows the answer at once and catches up with what the backend
 * derives on read (a draft's capacity, which the answers of post and lifecycle actions leave out).
 * The endpoints answer 404 while `marketing-automation` is off for the caller, and 403 to a RESTRICTED seat.
 *
 * Every write sends its body as the client vendor type: the controller's body-bearing endpoints consume
 * only `application/co.backbone.api.client.v1+json`, and axios' default `application/json` gets a 415.
 */

/** The property a campaign belongs to: `realtyId` in the property's own workspace. */
export interface SocialCampaignTarget {
  workspaceId: string
  realtyId: string
}

export const socialCampaignQueryKey = ({ workspaceId, realtyId }: SocialCampaignTarget) =>
  [EntityKeys.SOCIAL_CAMPAIGN, QueryType.GET_ONE, { workspaceId, realtyId }] as const

export const socialCampaignContentInventoryQueryKey = ({ workspaceId, realtyId }: SocialCampaignTarget) =>
  [EntityKeys.SOCIAL_CAMPAIGN_CONTENT_INVENTORY, QueryType.GET, { workspaceId, realtyId }] as const

/**
 * Creation writes the first plan in the same request (ISSUE-3984), so it may take a while; past this
 * the request is abandoned and the generation shows its error with a retry instead of spinning forever.
 */
export const CREATE_SOCIAL_CAMPAIGN_TIMEOUT_MS = 60_000

/**
 * How often a campaign whose story is still being written (ISSUE-3985) is re-read once the page shows it: the
 * generation waits for the story at most `STORY_WAIT_MS`, then the draft shows the template story until the backend
 * rewrites the untouched drafts with the story (or falls back after 15 minutes). Paused in a background tab.
 */
export const STORY_REFRESH_MS = 15_000

/**
 * How often a campaign is re-read while an AI action rewrites one of its posts' texts (ISSUE-3619): the answer usually
 * takes a few seconds. The backend reports an action unanswered after 5 minutes as FAILED, so the polling stops by
 * itself. Paused in a background tab.
 */
export const TEXT_ACTION_POLL_MS = 3_000

/** When the campaign query re-reads itself: quickly while a post's text is rewritten, slowly while the story is written. */
export const campaignRefetchIntervalOf = (campaign: SocialCampaignDTO | null | undefined): number | false => {
  if (campaign?.posts.some((post) => postTextStatusOf(post) === PostTextStatus.PENDING)) return TEXT_ACTION_POLL_MS
  if (campaign?.story?.status === StoryStatus.GENERATING) return STORY_REFRESH_MS
  return false
}

/**
 * How long the backend waits for an AI action's answer (`PostTextStatus.ACTION_TIMEOUT`): past it the action is FAILED,
 * so no read shows its post PENDING any more.
 */
export const TEXT_ACTION_TIMEOUT_MS = 5 * 60_000

/**
 * An AI action this page asked on a post's text (ISSUE-3619), followed until a read of the campaign shows it answered.
 * A read that left the server before the action was applied can land after the action's own answer and show the post
 * as it was, not PENDING: only a read after one that showed the post PENDING is taken as the answer.
 */
export interface AskedPostText {
  postId: string
  action: PostTextAction
  /** The post's text as the backend took the action */
  text: string
  /** A read showed the post PENDING: the next read that does not is the answer */
  pendingSeen: boolean
  /** When the backend took it (`Date.now()`) */
  askedAt: number
}

const NOTHING_ASKED: readonly AskedPostText[] = []

interface AskedTextsSlot {
  asked: readonly AskedPostText[]
  listeners: Set<() => void>
}

/** Per query client (the app's, or a test's own) and property */
const askedTexts = new WeakMap<QueryClient, Map<string, AskedTextsSlot>>()

const askedTextsSlot = (client: QueryClient, { workspaceId, realtyId }: SocialCampaignTarget): AskedTextsSlot => {
  const slots = askedTexts.get(client) ?? new Map<string, AskedTextsSlot>()
  askedTexts.set(client, slots)
  const key = `${workspaceId}/${realtyId}`
  const slot = slots.get(key) ?? { asked: NOTHING_ASKED, listeners: new Set<() => void>() }
  slots.set(key, slot)
  return slot
}

const setAskedTexts = (client: QueryClient, target: SocialCampaignTarget, asked: readonly AskedPostText[]) => {
  const slot = askedTextsSlot(client, target)
  const next = asked.length ? asked : NOTHING_ASKED
  if (slot.asked === next) return
  slot.asked = next
  slot.listeners.forEach((listener) => listener())
}

const isTextPending = (post: Pick<CampaignPostDTO, 'textStatus'>) => postTextStatusOf(post) === PostTextStatus.PENDING

/**
 * A read of the campaign against the actions asked: a post it shows PENDING was seen PENDING; one it shows otherwise
 * after that is answered. One it shows not PENDING before is a read older than the action: the post stays as the page
 * shows it (`shown`), unless the read's text moved on since (a later write), the backend's wait is over, or the page
 * does not show it PENDING either.
 */
export const followAskedTexts = (
  read: SocialCampaignDTO | null,
  shown: SocialCampaignDTO | null | undefined,
  asked: readonly AskedPostText[],
  now: number
): { read: SocialCampaignDTO | null, asked: readonly AskedPostText[] } => {
  const held = new Map<string, CampaignPostDTO>()
  const following: AskedPostText[] = []
  for (const item of asked) {
    const post = read?.posts.find((candidate) => candidate.id === item.postId)
    if (!post) continue
    if (isTextPending(post)) {
      following.push(item.pendingSeen ? item : { ...item, pendingSeen: true })
      continue
    }
    const kept = shown?.posts.find((candidate) => candidate.id === item.postId)
    if (item.pendingSeen || !kept || !isTextPending(kept) || post.text !== item.text || now - item.askedAt >= TEXT_ACTION_TIMEOUT_MS) continue
    held.set(item.postId, kept)
    following.push(item)
  }
  const unchanged = following.length === asked.length && following.every((item, index) => item === asked[index])
  return {
    read: read && held.size ? { ...read, posts: read.posts.map((post) => held.get(post.id) ?? post) } : read,
    asked: unchanged ? asked : following,
  }
}

/** A read of the campaign as the page takes it: a post whose text an action asked here waits for its answer */
const takeRead = (client: QueryClient, target: SocialCampaignTarget, read: SocialCampaignDTO | null) => {
  const { asked } = askedTextsSlot(client, target)
  if (asked.length === 0) return read
  const followed = followAskedTexts(read, client.getQueryData<SocialCampaignDTO | null>(socialCampaignQueryKey(target)), asked, Date.now())
  setAskedTexts(client, target, followed.asked)
  return followed.read
}

/**
 * The AI actions this page asked on the property's posts' texts and has not seen answered yet (ISSUE-3619). One
 * `pendingSeen` that leaves the list was answered (its post says READY or FAILED); one that leaves before was let go
 * without a read showing it PENDING (its text moved on, the backend's wait is over, the post is gone).
 */
export function useAskedSocialCampaignTexts(target: SocialCampaignTarget): readonly AskedPostText[] {
  const client = useQueryClient()
  const { workspaceId, realtyId } = target
  const subscribe = useCallback((listener: () => void) => {
    const { listeners } = askedTextsSlot(client, { workspaceId, realtyId })
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  }, [client, workspaceId, realtyId])
  const snapshot = useCallback(() => askedTextsSlot(client, { workspaceId, realtyId }).asked, [client, workspaceId, realtyId])
  return useSyncExternalStore(subscribe, snapshot)
}

export const isSocialCampaignNotFound = (error: unknown) =>
  axios.isAxiosError(error) && error.response?.status === 404

/**
 * A write the campaign's state refuses: a second creation (another tab, or a timed-out creation that went
 * through), or a change to a campaign changed elsewhere (e.g. ended in another tab).
 */
export const isSocialCampaignConflict = (error: unknown) =>
  axios.isAxiosError(error) && error.response?.status === 409

/**
 * A seat the endpoints refuse (`minSeatType = SPONSORED`: RESTRICTED). The tab is hidden from it, so a 403 is a
 * seat changed under an open page, which the page shows as its error: kept out of Sentry.
 */
export const isSocialCampaignForbidden = (error: unknown) =>
  axios.isAxiosError(error) && error.response?.status === 403

/** Kept out of Sentry: a conflict, which the page answers itself (it shows the campaign as the backend has it), and a refused seat. */
const EXPECTED_WRITE_ERRORS = {
  ignoreErrorFactory: (error: unknown) => isSocialCampaignConflict(error) || isSocialCampaignForbidden(error),
}

/**
 * The posts a launching action refused (422, ISSUE-3620): those that would go out and cannot be published, with
 * why. It can name drafts the request did not touch (one approved earlier without text): show every one of them.
 * Null for any other failure.
 */
export const postsNotPublishableOf = (error: unknown): PostsNotPublishableDTO | null => {
  if (!axios.isAxiosError(error) || error.response?.status !== 422) return null
  const posts: unknown = (error.response.data as Partial<PostsNotPublishableDTO> | undefined)?.posts
  // another shape is not ours to name: it takes the generic path, which reaches Sentry
  const named = Array.isArray(posts) && posts.length > 0 && posts.every((post) =>
    typeof post?.postId === 'string' && Array.isArray(post.reasons)
  )
  return named ? { posts: posts as PostsNotPublishableDTO['posts'] } : null
}

/**
 * A launching action answers a conflict or a refused seat, as the other writes, or posts it cannot publish, which the
 * page names: all kept out of Sentry.
 */
const REFUSAL_IS_EXPECTED = {
  ignoreErrorFactory: (error: unknown) => EXPECTED_WRITE_ERRORS.ignoreErrorFactory(error) || postsNotPublishableOf(error) !== null,
}

/** The text a post holds leaves nothing to rewrite (400): the editor says so */
export const isPostTextRefused = (error: unknown) =>
  axios.isAxiosError(error) && error.response?.status === 400

/** An AI action refused by the post's state or by its text: both said by the editor, kept out of Sentry. */
const TEXT_REFUSAL_IS_EXPECTED = { ignoreErrorFactory: (error: unknown) => isSocialCampaignConflict(error) || isPostTextRefused(error) }

/** The headers of a write: the role's Accept, as `useAPI` sets it, and the client vendor type for the body. */
const writeConfig = (roles: Auth0Roles, config?: AxiosRequestConfig): AxiosRequestConfig => ({
  ...config,
  headers: { Accept: API.decideRoleMimeType(roles), 'Content-Type': RoleMimetype.CLIENT },
})

/**
 * Puts a write's answer in the cache, then re-reads the campaign. The answers of post and lifecycle actions carry no
 * capacity: a draft keeps the one it had until the re-read brings the new one, so its plan section does not blink.
 */
const storeCampaign = (client: QueryClient, target: SocialCampaignTarget, campaign: SocialCampaignDTO) => {
  const queryKey = socialCampaignQueryKey(target)
  client.setQueryData<SocialCampaignDTO | null>(queryKey, (previous) =>
    campaign.capacity === null && campaign.status === CampaignStatus.DRAFT && previous?.id === campaign.id
      ? { ...campaign, capacity: previous.capacity }
      : campaign
  )
  client.invalidateQueries({ queryKey })
}

// ============================================================================
// QUERIES
// ============================================================================

/**
 * The property's campaign, `null` when it has none yet.
 *
 * A 404 is a state of the page (no campaign: the intro), not an incident: it is answered as `null`
 * and kept out of Sentry, as is a 403 (a refused seat), which stays an error. `useAPI` cannot pass that
 * reporting option, hence `API` directly (same as `useFetchConversation`). No retry: the page shows a
 * failure at once with its own Try again, and each retry would be one more Sentry event.
 *
 * A post whose text an AI action asked here keeps what the page shows until a read shows it PENDING (`followAskedTexts`):
 * a read older than the action is not its answer, and the post still PENDING keeps the polling on.
 */
export function useGetSocialCampaign(target: SocialCampaignTarget, enabled = true) {
  const { roles } = useAuth0()
  const client = useQueryClient()
  const { workspaceId, realtyId } = target

  return useQuery({
    queryKey: socialCampaignQueryKey(target),
    queryFn: async (): Promise<SocialCampaignDTO | null> => {
      let campaign: SocialCampaignDTO | null
      try {
        const response = await API.get<SocialCampaignDTO>(
          _getUrl(SocialCampaignEndpoint.CAMPAIGN, { workspaceId, realtyId }),
          { headers: { Accept: API.decideRoleMimeType(roles) } },
          {
            endpoint: SocialCampaignEndpoint.CAMPAIGN,
            ignoreErrorFactory: (error) => isSocialCampaignNotFound(error) || isSocialCampaignForbidden(error),
          }
        )
        campaign = response.data
      } catch (error) {
        if (!isSocialCampaignNotFound(error)) throw error
        campaign = null
      }
      return takeRead(client, { workspaceId, realtyId }, campaign)
    },
    enabled: enabled && !!workspaceId && !!realtyId,
    retry: false,
    refetchInterval: (query) => campaignRefetchIntervalOf(query.state.data),
  })
}

/**
 * What the property's gallery can feed into a campaign, per kind of content: the intro's facts, and a draft's
 * counts per kind ("7 photos", "5 of 7 photos", "made with AI"), which the campaign's capacity does not carry.
 * A 403 (a refused seat) is kept out of Sentry, hence `API` directly as in `useGetSocialCampaign`.
 */
export function useGetSocialCampaignContentInventory(target: SocialCampaignTarget, enabled = true) {
  const { roles } = useAuth0()
  const { workspaceId, realtyId } = target

  return useQuery({
    queryKey: socialCampaignContentInventoryQueryKey(target),
    queryFn: async () => {
      const response = await API.get<ContentInventoryDTO>(
        _getUrl(SocialCampaignEndpoint.CONTENT_INVENTORY, { workspaceId, realtyId }),
        { headers: { Accept: API.decideRoleMimeType(roles) } },
        { endpoint: SocialCampaignEndpoint.CONTENT_INVENTORY, ignoreErrorFactory: isSocialCampaignForbidden }
      )
      return response.data
    },
    enabled: enabled && !!workspaceId && !!realtyId,
    // the intro reads without its facts rather than waiting on retries, each one more Sentry event
    retry: false,
  })
}

// ============================================================================
// MUTATIONS
// ============================================================================

export interface CreateSocialCampaignVariables extends SocialCampaignTarget {
  body: CreateSocialCampaignDTO
}

/**
 * Creates the campaign as a draft holding its first plan; 409 when the property already has one, which the
 * generation answers by showing it: kept out of Sentry.
 */
export function useCreateSocialCampaign() {
  const api = useAPI<SocialCampaignEndpoint>()
  const { roles } = useAuth0()

  return getMutation<SocialCampaignDTO, CreateSocialCampaignVariables>(
    ({ workspaceId, realtyId, body }) => api.post<SocialCampaignDTO>(
      SocialCampaignEndpoint.CAMPAIGN,
      { workspaceId, realtyId },
      body,
      false,
      writeConfig(roles, { timeout: CREATE_SOCIAL_CAMPAIGN_TIMEOUT_MS }),
      EXPECTED_WRITE_ERRORS
    ),
    (client, { workspaceId, realtyId }, campaign: SocialCampaignDTO) => storeCampaign(client, { workspaceId, realtyId }, campaign)
  )
}

export interface UpdateSocialCampaignSettingsVariables extends SocialCampaignTarget {
  body: UpdateSocialCampaignSettingsDTO
}

/**
 * Networks (at least one, each once; 400 otherwise) re-target every post still ahead. A new language
 * rewrites a draft's untouched drafts; once launched it applies to the posts generated next. 409 once
 * ended, which the panel answers by saying so and showing the campaign: kept out of Sentry.
 */
export function useUpdateSocialCampaignSettings() {
  const api = useAPI<SocialCampaignEndpoint>()
  const { roles } = useAuth0()

  return getMutation<SocialCampaignDTO, UpdateSocialCampaignSettingsVariables>(
    ({ workspaceId, realtyId, body }) => api.put<SocialCampaignDTO>(
      SocialCampaignEndpoint.SETTINGS,
      { workspaceId, realtyId },
      body,
      false,
      writeConfig(roles),
      EXPECTED_WRITE_ERRORS
    ),
    (client, { workspaceId, realtyId }, campaign: SocialCampaignDTO) => storeCampaign(client, { workspaceId, realtyId }, campaign)
  )
}

export interface UpdateSocialCampaignPlanVariables extends SocialCampaignTarget {
  body: CampaignPlanRequestDTO
}

/**
 * The plan sentence of a draft, "[posts] across [weeks]", both always sent. Re-plans the draft: the posts the client
 * reviewed, edited or wrote, and those an order reserved, keep their date; the others are rebuilt. 409 once launched,
 * which the section answers by saying so and showing the campaign: kept out of Sentry.
 */
export function useUpdateSocialCampaignPlan() {
  const api = useAPI<SocialCampaignEndpoint>()
  const { roles } = useAuth0()

  return getMutation<SocialCampaignDTO, UpdateSocialCampaignPlanVariables>(
    ({ workspaceId, realtyId, body }) => api.put<SocialCampaignDTO>(
      SocialCampaignEndpoint.PLAN,
      { workspaceId, realtyId },
      body,
      false,
      writeConfig(roles),
      EXPECTED_WRITE_ERRORS
    ),
    (client, { workspaceId, realtyId }, campaign: SocialCampaignDTO) => storeCampaign(client, { workspaceId, realtyId }, campaign)
  )
}

export interface UpdateSocialCampaignContentKindVariables extends SocialCampaignTarget {
  kind: ContentKind
  included: boolean
}

/**
 * Includes a kind of content in the campaign or leaves it out; never changes the number of posts. A draft re-plans
 * (a kind left out takes its visuals out of the drafts not touched by hand); ticking a kind back on whose every
 * visual was left out brings all of them back. Photos cannot be left out (400). 409 once ended, which the section
 * answers by saying so and showing the campaign: kept out of Sentry.
 */
export function useUpdateSocialCampaignContentKind() {
  const api = useAPI<SocialCampaignEndpoint>()
  const { roles } = useAuth0()

  return getMutation<SocialCampaignDTO, UpdateSocialCampaignContentKindVariables>(
    ({ workspaceId, realtyId, kind, included }) => api.put<SocialCampaignDTO>(
      SocialCampaignEndpoint.CONTENT_KIND,
      { workspaceId, realtyId, kind },
      { included },
      false,
      writeConfig(roles),
      EXPECTED_WRITE_ERRORS
    ),
    (client, { workspaceId, realtyId }, campaign: SocialCampaignDTO) => storeCampaign(client, { workspaceId, realtyId }, campaign)
  )
}

export interface UpdateSocialCampaignContentKindVisualsVariables extends SocialCampaignTarget {
  kind: ContentKind
  /** The visuals of the kind left out, from the viewer's content inventory of the kind; empty keeps them all */
  excludedVisuals: VisualKeyDTO[]
}

/**
 * Keeps or leaves out the visuals of a kind of content one by one (ISSUE-3651): the body lists those left out, every
 * other visual of the kind the viewer is shown is kept, those of orders hidden from the viewer keep their state. Leaving
 * every visual out leaves the kind out, keeping one brings it back; photos keep at least one (400). A draft re-plans
 * the drafts not touched by hand. 409 once ended, which the section answers by saying so and showing the campaign:
 * kept out of Sentry.
 */
export function useUpdateSocialCampaignContentKindVisuals() {
  const api = useAPI<SocialCampaignEndpoint>()
  const { roles } = useAuth0()

  return getMutation<SocialCampaignDTO, UpdateSocialCampaignContentKindVisualsVariables>(
    ({ workspaceId, realtyId, kind, excludedVisuals }) => api.put<SocialCampaignDTO>(
      SocialCampaignEndpoint.CONTENT_KIND_VISUALS,
      { workspaceId, realtyId, kind },
      { excludedVisuals } satisfies ContentKindVisualsDTO,
      false,
      writeConfig(roles),
      EXPECTED_WRITE_ERRORS
    ),
    (client, { workspaceId, realtyId }, campaign: SocialCampaignDTO) => storeCampaign(client, { workspaceId, realtyId }, campaign)
  )
}

/**
 * A lifecycle action without a body (ISSUE-3620); each answers the campaign, 409 when not allowed in its
 * state, which the page answers by saying so and showing the campaign: kept out of Sentry.
 */
function useSocialCampaignLifecycleAction(endpoint: SocialCampaignEndpoint) {
  const api = useAPI<SocialCampaignEndpoint>()
  const { roles } = useAuth0()

  return getMutation<SocialCampaignDTO, SocialCampaignTarget>(
    ({ workspaceId, realtyId }) => api.post<SocialCampaignDTO>(endpoint, { workspaceId, realtyId }, {}, false, writeConfig(roles), EXPECTED_WRITE_ERRORS),
    (client, { workspaceId, realtyId }, campaign: SocialCampaignDTO) => storeCampaign(client, { workspaceId, realtyId }, campaign)
  )
}

/** Holds the scheduled posts; a post whose time passes during the pause is never published. */
export const usePauseSocialCampaign = () => useSocialCampaignLifecycleAction(SocialCampaignEndpoint.PAUSE)

/** Carries on with the posts still ahead; nothing is rescheduled. */
export const useResumeSocialCampaign = () => useSocialCampaignLifecycleAction(SocialCampaignEndpoint.RESUME)

/** Ends a launched campaign: nothing not yet published will be. */
export const useEndSocialCampaign = () => useSocialCampaignLifecycleAction(SocialCampaignEndpoint.END)

/** Reopens an ended campaign, live with nothing ahead (quiet). */
export const useReopenSocialCampaign = () => useSocialCampaignLifecycleAction(SocialCampaignEndpoint.REOPEN)

/**
 * Approves every draft and schedules them all ("Approve all", and "Launch campaign" once each one is approved); a
 * draft campaign goes live. Posts reserved for an order keep waiting; a draft whose time is less than the edit freeze
 * ahead is not published. 422 (`postsNotPublishableOf`), before anything is approved, when a draft that would go out
 * cannot be published; 409 once ended; 403 to a refused seat. All answered by the page: kept out of Sentry.
 */
export function useApproveAllSocialCampaignPosts() {
  const api = useAPI<SocialCampaignEndpoint>()
  const { roles } = useAuth0()

  return getMutation<SocialCampaignDTO, SocialCampaignTarget>(
    ({ workspaceId, realtyId }) => api.post<SocialCampaignDTO>(
      SocialCampaignEndpoint.APPROVE_ALL,
      { workspaceId, realtyId },
      {},
      false,
      writeConfig(roles),
      REFUSAL_IS_EXPECTED
    ),
    (client, { workspaceId, realtyId }, campaign: SocialCampaignDTO) => storeCampaign(client, { workspaceId, realtyId }, campaign)
  )
}

export interface GenerateSocialCampaignPostsVariables extends SocialCampaignTarget {
  body: GenerateSocialCampaignPostsDTO
}

/**
 * "Generate new posts" on a launched campaign (ISSUE-3984): the story goes on from the day after the last post, or
 * from tomorrow, as drafts to approve. 409 on a draft or an ended campaign, which the page answers: kept out of Sentry.
 * Its dialog cannot be closed while it is on its way: abandoned past the creation's timeout, as a failure.
 */
export function useGenerateSocialCampaignPosts() {
  const api = useAPI<SocialCampaignEndpoint>()
  const { roles } = useAuth0()

  return getMutation<SocialCampaignDTO, GenerateSocialCampaignPostsVariables>(
    ({ workspaceId, realtyId, body }) => api.post<SocialCampaignDTO>(
      SocialCampaignEndpoint.GENERATE,
      { workspaceId, realtyId },
      body,
      false,
      writeConfig(roles, { timeout: CREATE_SOCIAL_CAMPAIGN_TIMEOUT_MS }),
      EXPECTED_WRITE_ERRORS
    ),
    (client, { workspaceId, realtyId }, campaign: SocialCampaignDTO) => storeCampaign(client, { workspaceId, realtyId }, campaign)
  )
}

export interface SocialCampaignPostTarget extends SocialCampaignTarget {
  postId: string
}

/**
 * Approves a draft in the editor (ISSUE-3624). The last draft decided schedules every draft and launches a draft
 * campaign; only then can it be refused (422, `postsNotPublishableOf`). 409 once the post is no longer a draft or the
 * campaign ended. Both answered by the editor: kept out of Sentry.
 */
export function useApproveSocialCampaignPost() {
  const api = useAPI<SocialCampaignEndpoint>()
  const { roles } = useAuth0()

  return getMutation<SocialCampaignDTO, SocialCampaignPostTarget>(
    ({ workspaceId, realtyId, postId }) => api.post<SocialCampaignDTO>(
      SocialCampaignEndpoint.APPROVE_POST,
      { workspaceId, realtyId, postId },
      {},
      false,
      writeConfig(roles),
      REFUSAL_IS_EXPECTED
    ),
    (client, { workspaceId, realtyId }, campaign: SocialCampaignDTO) => storeCampaign(client, { workspaceId, realtyId }, campaign)
  )
}

/** Takes an approval back while the post is a draft; 409 once scheduled, which the editor answers: kept out of Sentry. */
export function useUndoSocialCampaignPostApproval() {
  const api = useAPI<SocialCampaignEndpoint>()
  const { roles } = useAuth0()

  return getMutation<SocialCampaignDTO, SocialCampaignPostTarget>(
    ({ workspaceId, realtyId, postId }) => api.post<SocialCampaignDTO>(
      SocialCampaignEndpoint.UNDO_POST_APPROVAL,
      { workspaceId, realtyId, postId },
      {},
      false,
      writeConfig(roles),
      EXPECTED_WRITE_ERRORS
    ),
    (client, { workspaceId, realtyId }, campaign: SocialCampaignDTO) => storeCampaign(client, { workspaceId, realtyId }, campaign)
  )
}

export interface UpdateSocialCampaignPostVariables extends SocialCampaignPostTarget {
  /** Only what changed: an empty change is a 400 */
  body: UpdateCampaignPostDTO
}

/**
 * Changes a post (its date, time, networks, visuals or text). A post not published comes back with a new date or time.
 * 422 when a post that goes out could not be published as changed, 409 once it can no longer be changed (inside the
 * edit freeze, gone, or the campaign ended): both answered by the editor, kept out of Sentry.
 */
export function useUpdateSocialCampaignPost() {
  const api = useAPI<SocialCampaignEndpoint>()
  const { roles } = useAuth0()

  return getMutation<SocialCampaignDTO, UpdateSocialCampaignPostVariables>(
    ({ workspaceId, realtyId, postId, body }) => api.patch<SocialCampaignDTO>(
      SocialCampaignEndpoint.POST,
      { workspaceId, realtyId, postId },
      body,
      false,
      writeConfig(roles),
      REFUSAL_IS_EXPECTED
    ),
    (client, { workspaceId, realtyId }, campaign: SocialCampaignDTO) => storeCampaign(client, { workspaceId, realtyId }, campaign)
  )
}

/**
 * Removes a post for good, once the client confirmed. Removing the last draft still to decide schedules the approved
 * others and launches a draft campaign (422 when one of them cannot be published); 409 once it can no longer be
 * removed. Both answered by the editor: kept out of Sentry.
 */
export function useRemoveSocialCampaignPost() {
  const api = useAPI<SocialCampaignEndpoint>()

  return getMutation<SocialCampaignDTO, SocialCampaignPostTarget>(
    ({ workspaceId, realtyId, postId }) => api.delete<SocialCampaignDTO>(
      SocialCampaignEndpoint.POST,
      { workspaceId, realtyId, postId },
      false,
      undefined,
      REFUSAL_IS_EXPECTED
    ),
    (client, { workspaceId, realtyId }, campaign: SocialCampaignDTO) => storeCampaign(client, { workspaceId, realtyId }, campaign)
  )
}

export interface AddSocialCampaignPostVariables extends SocialCampaignTarget {
  body: CreateCampaignPostDTO
}

/**
 * Adds a post the client wrote, scheduled at once (nothing to approve). 422 when none of its networks can publish its
 * visuals, 409 once the campaign ended: both answered by the editor, kept out of Sentry.
 */
export function useAddSocialCampaignPost() {
  const api = useAPI<SocialCampaignEndpoint>()
  const { roles } = useAuth0()

  return getMutation<SocialCampaignDTO, AddSocialCampaignPostVariables>(
    ({ workspaceId, realtyId, body }) => api.post<SocialCampaignDTO>(
      SocialCampaignEndpoint.POSTS,
      { workspaceId, realtyId },
      body,
      false,
      writeConfig(roles),
      REFUSAL_IS_EXPECTED
    ),
    (client, { workspaceId, realtyId }, campaign: SocialCampaignDTO) => storeCampaign(client, { workspaceId, realtyId }, campaign)
  )
}

export interface RequestSocialCampaignPostTextVariables extends SocialCampaignPostTarget {
  action: PostTextAction
}

/**
 * An AI action on a post's text (ISSUE-3619): answers at once (202) with the campaign, the post's `textStatus` PENDING;
 * the campaign query then polls until it is READY (the new text is in) or FAILED (the text is kept). 409 once the
 * campaign ended, for a post sent, skipped or waiting for its order, or while another action runs on it; 400 when the
 * text leaves nothing to rewrite (nothing to shorten, no room left under the network's limit). Both answered by the
 * editor: kept out of Sentry. An action answered PENDING is followed until a read shows it answered
 * (`useAskedSocialCampaignTexts`); one answered otherwise (FAILED at once, as it could not be sent) has nothing to wait for.
 */
export function useRequestSocialCampaignPostText() {
  const api = useAPI<SocialCampaignEndpoint>()
  const { roles } = useAuth0()

  return getMutation<SocialCampaignDTO, RequestSocialCampaignPostTextVariables>(
    ({ workspaceId, realtyId, postId, action }) => api.post<SocialCampaignDTO>(
      SocialCampaignEndpoint.POST_TEXT_ACTION,
      { workspaceId, realtyId, postId, action },
      {},
      false,
      writeConfig(roles),
      TEXT_REFUSAL_IS_EXPECTED
    ),
    (client, { workspaceId, realtyId, postId, action }, campaign: SocialCampaignDTO) => {
      const target = { workspaceId, realtyId }
      const post = campaign.posts.find((item) => item.id === postId)
      // followed before the answer is stored: the read it asks for is already measured against it
      if (post && isTextPending(post)) {
        const others = askedTextsSlot(client, target).asked.filter((item) => item.postId !== postId)
        setAskedTexts(client, target, [...others, { postId, action, text: post.text, pendingSeen: false, askedAt: Date.now() }])
      }
      storeCampaign(client, target, campaign)
    }
  )
}
